<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        :class="{ 'opacity-40 pointer-events-none': isSameName }"
        @click="handleSave"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Rename '{{ props.item.name }}'
  </DialogHeader>
  <DialogContent>
    <UiInput
      v-model="name"
      label="Folder name"
      :disabled="loading"
      :errorMsg="error"
    />
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      primary
      :disabled="isSameName"
      :loading="loading"
      @click="handleSave"
    >
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, UiButton, UiInput } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { computed, ref } from 'vue'
import {
  SearchItemsDocument,
  SearchSort,
  useRenameFolderMutation,
  type SearchItemsQuery,
  type SearchItemsQueryVariables,
} from '@/shared/graphql/legacy/generated'
import type { Folder } from '@/shared/graphql/router/graphql'
import gql from 'graphql-tag'
import { useRoute } from 'vue-router'
import { useExplorerStore } from '@/features/explorer/store'
import { renameFolderInExplorerCache } from '@/features/explorer/utils/explorerCache'

const { close } = useDialog()

const props = defineProps<{
  item: Folder
}>()

const { mutate: renameFolder, loading } = useRenameFolderMutation()

const route = useRoute()
const currentFolderStore = useExplorerStore()

const name = ref(props.item.name)
const error = ref('')

const isSameName = computed(() => name.value.trim() === props.item.name.trim())

const handleSave = async () => {
  try {
    await renameFolder(
      {
        id: props.item.id,
        newFolderName: name.value,
      },
      {
        update() {
          renameFolderInExplorerCache(props.item.id, name.value)

          const newUrl = new URL(window.location.href)

          if (newUrl.pathname.includes(encodeURIComponent(props.item.name))) {
            const folderPath = currentFolderStore.currentFolder?.path || '/'
            const parentPath =
              folderPath === '/'
                ? '/'
                : folderPath.substring(0, folderPath.lastIndexOf('/') + 1) ||
                  '/'
            newUrl.pathname = `/home${parentPath}${name.value}`
            window.history.pushState({}, '', newUrl.toString())
          }
        },
      }
    )

    close()
  } catch (err) {
    console.error(err)

    if (err instanceof Error) {
      if (err.message.includes('already')) {
        error.value = 'A folder with this name is already taken'
      }
    }
  }
}
</script>
